import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, Leaf, Mail, Phone } from "lucide-react";

export default function TermsOfService() {
  const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing and using Agrivestia ('Platform', 'Service', 'we', 'us', 'our'), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these terms, please do not use the Platform. We reserve the right to update these terms at any time. Continued use of the Platform after changes constitutes your acceptance of the updated Terms. Your use of the Platform is also subject to our Privacy Policy and any other policies posted on the Platform.",
    },
    {
      title: "2. Eligibility and Account Registration",
      content: `To use Agrivestia, you must:

a) Age Requirement:
• Be at least 18 years old
• Possess the legal right to enter into contracts in your jurisdiction

b) Account Registration:
• Provide accurate, current, and complete information
• Maintain the confidentiality of your account credentials
• Accept responsibility for all activities under your account
• Notify us immediately of unauthorized access to your account
• You are responsible for all transactions made through your account

c) Account Types:
• Agrivestia supports accounts for individuals, farmers, businesses, and organizations
• Each account holder certifies they have the authority to represent their entity
• Fraudulent registration may result in account termination and legal action`,
    },
    {
      title: "3. Intellectual Property Rights",
      content: `a) Platform Ownership:
• All content on Agrivestia (design, text, graphics, logos, software, code) is owned by or licensed to Agrivestia
• You may not reproduce, modify, distribute, or use any content without explicit written permission
• Agrivestia™, AGRIVESTIA®, and related marks are trademarks of Agrivestia

b) User-Generated Content:
• You retain ownership of content you create (product listings, reviews, messages)
• By posting content, you grant Agrivestia a worldwide, non-exclusive license to use, modify, and display your content
• You warrant that your content does not infringe third-party rights
• You agree not to post content that is illegal, defamatory, or violates others' rights

c) Restrictions:
• You may not use our Platform to create competing services
• Scraping, data mining, or reverse-engineering is strictly prohibited
• Do not remove or alter any proprietary notices or labels`,
    },
    {
      title: "4. User Conduct and Responsibilities",
      content: `You agree to use Agrivestia responsibly and in compliance with all applicable laws. You shall not:

a) Prohibited Activities:
• Use the Platform for illegal or fraudulent purposes
• Engage in money laundering or terrorist financing
• Manipulate prices or create artificial demand
• Misrepresent products, services, or business credentials
• Harass, threaten, or abuse other users
• Interfere with or disrupt the Platform's functionality
• Create multiple accounts to evade restrictions or manipulate the system

b) Compliance:
• Comply with all local, national, and international regulations
• Follow AML (Anti-Money Laundering) and KYC (Know Your Customer) requirements
• Declare accurate tax information and financial details
• Report suspicious activities to our support team
• Cooperate with investigations and audits

c) Content Standards:
• Ensure all product descriptions are accurate and honest
• Provide genuine contact information
• Respond to customer inquiries professionally and timely
• Honor agreements and contracts made through the Platform`,
    },
    {
      title: "5. Transaction Terms",
      content: `a) Transaction Mechanics:
• Agrivestia facilitates transactions between buyers and sellers
• All prices quoted are subject to availability and market conditions
• Supply and demand may affect pricing in real-time marketplace listings

b) Buyer Protections:
• Escrow services protect buyer funds until delivery/completion
• Dispute resolution process for transaction issues
• Right to cancel transactions under specified conditions
• Refund policies as detailed in our Marketplace Guidelines

c) Seller Obligations:
• Provide accurate and complete product information
• Fulfill orders according to agreed specifications
• Deliver products within the agreed timeframe
• Maintain product quality standards
• Respond to buyer inquiries and issues professionally

d) Payment Processing:
• All transactions are processed through secure payment gateways
• Payment providers' terms and conditions apply
• Agrivestia charges transaction fees as per our published fee schedule
• Fees may be adjusted with 30 days' notice

e) Dispute Resolution:
• Disputes are resolved through Agrivestia's mediation process
• Both parties agree to work toward fair resolution
• Escalation to legal proceedings only after mediation fails
• Each party covers their own legal costs unless otherwise ordered`,
    },
    {
      title: "6. Limitation of Liability",
      content: `To the maximum extent permitted by law:

a) As-Is Basis:
• Agrivestia is provided \"as-is\" and \"as-available\" without warranties
• We do not warrant that the Platform is error-free, secure, or uninterrupted
• We do not guarantee specific results or outcomes from using the Platform

b) Exclusion of Damages:
• Agrivestia shall not be liable for indirect, incidental, special, or consequential damages
• We are not liable for lost profits, data loss, business interruption, or lost opportunities
• Our total liability is limited to the fees you paid in the last 12 months

c) Third-Party Content:
• We are not responsible for third-party information, products, or services
• User-generated content is the responsibility of the user
• We are not liable for transactions between users or disputes arising from them

d) Force Majeure:
• Agrivestia is not liable for disruptions due to natural disasters, wars, or circumstances beyond our control
• Services may be suspended during emergencies or system maintenance with advance notice when possible`,
    },
    {
      title: "7. Termination and Account Suspension",
      content: `a) User Termination:
• You may terminate your account anytime by contacting support
• Upon termination, you retain rights to your data (subject to legal holds)
• Outstanding transactions must be completed or cancelled before termination

b) Agrivestia Termination:
• We may suspend or terminate accounts that violate these Terms
• Grounds for termination include fraud, illegal activity, or repeated violations
• Termination may be immediate for serious violations or after 30-day notice for other breaches
• Users will be notified of termination and the reason when possible

c) Effects of Termination:
• Your access to the Platform is immediately revoked
• Unpaid fees remain due and payable
• We may retain your data for legal compliance and fraud prevention as required by law
• Clauses surviving termination include liability limitations, intellectual property, and dispute resolution`,
    },
    {
      title: "8. Indemnification",
      content:
        "You agree to indemnify, defend, and hold harmless Agrivestia, its officers, directors, employees, agents, and successors from any claims, damages, losses, or expenses (including legal fees) arising from: (a) your use of the Platform, (b) your breach of these Terms, (c) your infringement of third-party rights, (d) your violation of applicable laws, or (e) content you post or transactions you conduct. This indemnification obligation survives termination of your account.",
    },
    {
      title: "9. Dispute Resolution",
      content: `a) Informal Resolution:
• First, contact our support team at support@agrivestia.com
• We aim to resolve issues within 14 days

b) Mediation:
• If unresolved, disputes may proceed to mediation
• Both parties agree to participate in good faith negotiation
• Mediation costs are shared equally unless otherwise agreed

c) Arbitration/Legal Proceedings:
• If mediation fails, disputes shall be resolved according to Nigerian law
• Jurisdiction: Nigerian courts have exclusive jurisdiction
• You waive the right to jury trial and class action proceedings
• Each party covers their own costs unless a court orders otherwise

d) Small Claims:
• Either party may pursue claims in small claims court without arbitration`,
    },
    {
      title: "10. Disclaimer of Warranties",
      content: `AGRIVESTIA IS PROVIDED \"AS-IS\" AND \"AS-AVAILABLE.\" WE MAKE NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT: (a) THE PLATFORM WILL MEET YOUR REQUIREMENTS, (b) SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, (c) DEFECTS WILL BE CORRECTED, OR (d) THE PLATFORM IS FREE OF VIRUSES OR HARMFUL COMPONENTS. YOUR USE OF THE PLATFORM IS AT YOUR OWN RISK. SOME JURISDICTIONS DO NOT ALLOW EXCLUSION OF IMPLIED WARRANTIES, SO SOME OF THIS DISCLAIMER MAY NOT APPLY TO YOU.`,
    },
    {
      title: "11. Price and Fee Structure",
      content: `a) Transaction Fees:
• Agrivestia charges transaction fees on successful trades
• Fee amounts are displayed before transaction confirmation
• Fees support platform maintenance, security, and support services

b) Price Changes:
• We may adjust fees with 30 days' notice
• Price changes apply to new transactions after the effective date
• Existing transactions are not affected by price changes

c) Payment Terms:
• Fees are deducted from transaction amounts or billed separately
• Late payments may result in account restriction
• Refunds are not available for disputed fees (except in cases of overcharging)

d) Subscription Services:
• Premium features, if available, are charged on a subscription basis
• Subscriptions auto-renew unless cancelled before the renewal date
• Cancellations take effect at the end of the current billing period`,
    },
    {
      title: "12. Changes to Terms and Services",
      content:
        "Agrivestia reserves the right to modify these Terms, the Platform, and its services at any time. Material changes will be communicated via email or prominent notice on the Platform. Your continued use after changes constitutes acceptance. Agrivestia may also modify, suspend, or discontinue features or services with notice. We are not liable for any modifications or discontinuations.",
    },
    {
      title: "13. Severability and Waiver",
      content:
        "If any provision of these Terms is deemed unenforceable, the remaining provisions continue in full force. Unenforceability in one jurisdiction does not affect enforceability elsewhere. If we fail to enforce a provision, it does not waive our right to enforce it later. No waiver of any provision is effective unless in writing and signed by both parties.",
    },
    {
      title: "14. Entire Agreement",
      content:
        "These Terms, along with our Privacy Policy, Cookie Policy, and any marketplace guidelines, constitute the entire agreement between you and Agrivestia. They supersede all prior agreements, understandings, and negotiations. No party is relying on representations outside of these written documents.",
    },
    {
      title: "15. Contact Information",
      content:
        "For questions about these Terms of Service, please contact us:\n\nEmail: legal@agrivestia.com\nPhone: +234 800 AGRIVESTIA\nSupport: support@agrivestia.com\n\nWe will respond to all inquiries within 7-10 business days.",
    },
    {
      title: "16. Blog Content and User Contributions",
      content: `a) Contribution Submission:
• Users may submit agricultural content, articles, and insights for consideration to support@agrivestia.com
• All submissions must be original and not infringe on any third-party intellectual property rights
• You grant Agrivestia a non-exclusive, perpetual, royalty-free license to publish, edit, and distribute your content across our Platform

b) Editorial Review:
• All guest posts are subject to mandatory editorial review and approval
• Agrivestia reserves the right to reject any submission for any reason
• We may edit content for clarity, length, formatting, or to ensure compliance with Platform standards
• Publication does not constitute an endorsement of the contributor's views or claims

c) Prohibited Content:
• Content must not contain spam, marketing materials, or unauthorized commercial promotions
• Discriminatory, offensive, or illegal content is strictly prohibited
• Submissions must not provide financial advice or guarantee specific investment outcomes`,
    },
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet>
        <title>Terms of Service — Agrivestia</title>
      </Helmet>

      {/* Header */}
      <div style={{ background: "var(--gradient-primary)", color: "white", padding: "60px 32px", textAlign: "center" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "16px" }}>
            <Leaf size={32} />
            <span style={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>
          <h1 style={{ fontSize: "42px", fontWeight: 800, marginBottom: "16px", letterSpacing: "-0.02em" }}>Terms of Service</h1>
          <p style={{ fontSize: "16px", opacity: 0.9 }}>Last updated: February 24, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "60px 32px" }}>
        <div style={{ marginBottom: "48px", padding: "24px", background: "white", borderRadius: "12px", border: "1px solid var(--color-gray-100)", boxShadow: "0 4px 6px rgba(0,0,0,0.05)" }}>
          <p style={{ color: "var(--color-gray-700)", lineHeight: 1.8, fontSize: "15px" }}>
            These Terms of Service govern your use of the Agrivestia platform and services. We are committed to providing a fair,
            secure, and transparent marketplace for agricultural trading. By using Agrivestia, you accept full responsibility for your
            actions and agree to comply with all applicable laws and regulations. Please read these terms carefully before proceeding.
          </p>
        </div>

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {sections.map((section, index) => (
            <div key={index} style={{ background: "white", border: "1px solid var(--color-gray-100)", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
              <button
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                style={{
                  width: "100%",
                  padding: "20px",
                  background: "white",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-gray-50)"}
                onMouseLeave={e => e.currentTarget.style.background = "white"}
              >
                <h3 style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)", textAlign: "left" }}>
                  {section.title}
                </h3>
                <ChevronDown
                  size={20}
                  style={{
                    color: "var(--color-primary)",
                    transition: "transform 0.3s",
                    transform: expandedSection === index ? "rotate(180deg)" : "rotate(0deg)",
                    flexShrink: 0,
                  }}
                />
              </button>

              {expandedSection === index && (
                <div style={{ padding: "24px", borderTop: "1px solid var(--color-gray-100)", background: "var(--color-gray-50)" }}>
                  <p style={{ color: "var(--color-gray-700)", lineHeight: 1.8, fontSize: "14px", whiteSpace: "pre-wrap" }}>
                    {section.content}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div style={{ marginTop: "60px", padding: "40px", background: "var(--color-primary-light)", borderRadius: "16px", border: "1px solid var(--color-accent-light)" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary-dark)", marginBottom: "24px" }}>Questions About Our Terms?</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px" }}>
            <div style={{ display: "flex", gap: "12px" }}>
              <Mail size={20} style={{ color: "var(--color-primary)", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-gray-900)", marginBottom: "4px" }}>Legal Team</div>
                <a href="mailto:legal@agrivestia.com" style={{ color: "var(--color-primary)", textDecoration: "none" }}>
                  legal@agrivestia.com
                </a>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <Phone size={20} style={{ color: "var(--color-primary)", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-gray-900)", marginBottom: "4px" }}>Support</div>
                <a href="tel:+234800AGRIVESTIA" style={{ color: "var(--color-primary)", textDecoration: "none" }}>
                  +234 800 AGRIVESTIA
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div style={{ marginTop: "60px", paddingTop: "40px", borderTop: "1px solid var(--color-gray-200)", textAlign: "center" }}>
          <p style={{ color: "var(--color-gray-600)", marginBottom: "16px" }}>Related Policies</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", flexWrap: "wrap" }}>
            <a href="/privacy-policy" style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: 600 }}>
              Privacy Policy
            </a>
            <span style={{ color: "var(--color-gray-300)" }}>•</span>
            <a href="/" style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: 600 }}>
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
